import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { Add, Settings, People, Shield } from "@mui/icons-material";
import { Typography, Button, Box, Chip, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router";

import { Permission } from "@/components/UserManagement/types";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IAlertGroup } from "@/types";

const AlertGroupManagement = () => {
  const { currentUserGroups, currentUser } = useContext(AuthStateContext);
  const userHasGroupCreatePermission = currentUser?.permissions.some(
    (permission) => permission.title === Permission.ALERT_CREATE_GROUP,
  );

  const navigate = useNavigate();

  const serviceGroups = currentUserGroups.filter(
    (group) => group.is_self_group === false,
  );

  const topPageRef = useRef<HTMLDivElement | null>(null);

  const columns: GridColDef<IAlertGroup>[] = [
    {
      field: "name",
      headerName: "Group Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "permissions",
      headerName: "Your Permissions",
      flex: 1.5,
      minWidth: 300,
      renderCell: (params) => {
        const group = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
            {group.manage_group && (
              <Tooltip title="Can manage group settings">
                <Chip
                  icon={<Settings sx={{ fontSize: 14 }} />}
                  label="Manage Group"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
            {group.manage_admins && (
              <Tooltip title="Can manage admin permissions">
                <Chip
                  icon={<Shield sx={{ fontSize: 14 }} />}
                  label="Manage Admins"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
            {group.manage_members && (
              <Tooltip title="Can add/remove members">
                <Chip
                  icon={<People sx={{ fontSize: 14 }} />}
                  label="Manage Members"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
            {group.moderate_bulletin_admittance && (
              <Tooltip title="Can approve/reject bulletins">
                <Chip
                  label="Moderate"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
            {group.bypass_moderation && (
              <Tooltip title="Can bypass moderation">
                <Chip
                  label="Bypass"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
            {!group.manage_group &&
              !group.manage_admins &&
              !group.manage_members &&
              !group.moderate_bulletin_admittance &&
              !group.bypass_moderation && (
                <Chip
                  label="Member"
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const group = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {group.is_public && (
              <Chip label="Public" size="small" color="primary" />
            )}
            {group.is_police_service && (
              <Chip label="Police" size="small" color="info" />
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <div className="users_list_container">
      <div id="top-page-div" ref={topPageRef}></div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Your Groups
        </Typography>
        {userHasGroupCreatePermission && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => navigate("/rubialert/manage-groups/create")}
          >
            Create Group
          </Button>
        )}
      </Box>

      <DataGrid
        rows={serviceGroups}
        columns={columns}
        rowSelection={false}
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              cursor: "pointer",
              py: 1,
            },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
        onRowClick={(row) => {
          navigate(`${row.id}/`);
        }}
      />
    </div>
  );
};

export default AlertGroupManagement;
